import { assistedAffiliationClient } from "./assistedAffiliationClient";
import { getAssistedAffiliationHost } from "./getAssistedAffiliationHost";
import {
  ApiEnvelope,
  AssistedAffiliationListParams,
  AssistedAffiliationListResponse,
} from "./types";

const assistedAffiliationListEndpoint = "/v1/assisted-affiliation/all";

// El servicio rechaza una consulta de más de 100 filas ("'pageSize' debe estar entre 1 y 100").
export const SERVICE_MAX_PAGE_SIZE = 100;

/**
 * Consulta paginada de solicitudes. Apunta al mock o al servicio definitivo sin cambiar este archivo.
 * Un bloque mayor que el que acepta el servicio se pide en páginas consecutivas de ese tamaño, en
 * paralelo, y se une aquí: la pantalla no depende del límite.
 * @param params - `pageNumber` empieza en 0, `pageSize` es su tamaño, y los filtros son opcionales.
 * @returns Los items y totales ya desenvueltos; relanza el error para que la pantalla lo traduzca.
 */
export const getAssistedAffiliationListService = async (
  params: AssistedAffiliationListParams,
): Promise<AssistedAffiliationListResponse> => {
  const host = await getAssistedAffiliationHost();

  // El endpoint es POST porque el body lleva la paginación y los filtros. Los filtros ya vienen con
  // los nombres del contrato y solo con las claves que se pidieron, así que viajan tal cual.
  const fetchPage = async (pageNumber: number, pageSize: number) => {
    const response = await assistedAffiliationClient.post<
      ApiEnvelope<AssistedAffiliationListResponse>
    >(`${host}${assistedAffiliationListEndpoint}`, { ...params, pageNumber, pageSize });

    // El envoltorio se abre aquí para que la pantalla solo vea el listado: si otra API lo adopta o lo
    // deja, el cambio no sale de esta capa.
    return response.data.data;
  };

  // Partir el bloque solo cuadra si es múltiplo del límite: si no, se pide tal cual y el servicio
  // responde su error, que la pantalla muestra.
  if (params.pageSize <= SERVICE_MAX_PAGE_SIZE || params.pageSize % SERVICE_MAX_PAGE_SIZE !== 0) {
    return fetchPage(params.pageNumber, params.pageSize);
  }

  const parts = params.pageSize / SERVICE_MAX_PAGE_SIZE;
  const pages = await Promise.all(
    Array.from({ length: parts }, (_, index) =>
      fetchPage(params.pageNumber * parts + index, SERVICE_MAX_PAGE_SIZE)
    )
  );
  const items = pages.flatMap((page) => page?.items ?? []);
  const totalElements = pages[0]?.total_elements ?? 0;
  const totalPages = Math.ceil(totalElements / params.pageSize);

  // Los totales y la navegación se expresan en bloques del tamaño pedido, como si fuera una sola página.
  return {
    items,
    total_elements: totalElements,
    total_pages: totalPages,
    number_of_elements: items.length,
    current_page: params.pageNumber,
    next_page: params.pageNumber + 1 < totalPages ? params.pageNumber + 1 : null,
    previous_page: params.pageNumber > 0 ? params.pageNumber - 1 : null,
  };
};
